var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { SalesRepDataActionType } from "./actions/actionTypes";
import { parseDisplayDateValue } from "../component/SharedFunctions";
import { cloneSTOrderDetailsReportsPayload, getSTOrderDetailsOrderEffectiveDateRaw, getSTOrderDetailsTabSlicesFromPayload, isMeaningfulSTOrderDetailsDateValue, isSTOrderDetailsOrderInCalendarRange, prepareSTOrderDetailsReportsPayload, } from "../component/model/STOrderDetailsModel";
import { getSTOrderDetailsRefundRowsFromPayload, mapRefundGridRowToStReportsOrderRow, parseStReportsRefundGridRow, } from "../component/model/stOrderDetailsRefundGrid";
import { formatDateForAPI, todayAtMidnight } from "../component/SalesOrderDetails/STReports/DateRangePicker/dateRangeFormatUtils";
import { logClientRuntimeError } from "../services/clientErrorLog";
import { buildSTReportsOrdersCacheRangeKey } from "./stReportsOrdersCache";
function daysAgoFromToday(days) {
    try {
        var today = todayAtMidnight();
        var d = new Date(today);
        d.setDate(today.getDate() - days);
        return d;
    }
    catch (error) {
        logClientRuntimeError("daysAgoFromToday", error);
        return todayAtMidnight();
    }
}
/** Today, yesterday, and last 7 days — seeded from the default 30-day bootstrap (no extra API). */
export function getSTOrderDetailsBootstrapPresetRanges() {
    try {
        var today = todayAtMidnight();
        var yesterday = daysAgoFromToday(1);
        var last7Start = daysAgoFromToday(6);
        var presets = [
            { startDate: today, endDate: today },
            { startDate: yesterday, endDate: yesterday },
            { startDate: last7Start, endDate: today },
        ];
        return presets.map(function (preset) { return (__assign(__assign({}, preset), { dateFrom: formatDateForAPI(preset.startDate), dateTo: formatDateForAPI(preset.endDate) })); });
    }
    catch (error) {
        logClientRuntimeError("getSTOrderDetailsBootstrapPresetRanges", error);
        var today = todayAtMidnight();
        return [
            {
                startDate: today,
                endDate: today,
                dateFrom: formatDateForAPI(today),
                dateTo: formatDateForAPI(today),
            },
        ];
    }
}
function hasExportOrderBuckets(source) {
    var _a;
    try {
        var buckets = (_a = source.exportOrderBuckets) !== null && _a !== void 0 ? _a : source.ExportOrderBuckets;
        return buckets != null && typeof buckets === "object";
    }
    catch (error) {
        logClientRuntimeError("hasExportOrderBuckets", error);
        return false;
    }
}
function canSeedPresetCachesFromBootstrap(source) {
    try {
        if (hasExportOrderBuckets(source)) {
            return true;
        }
        return getSTOrderDetailsTabSlicesFromPayload(source) != null;
    }
    catch (error) {
        logClientRuntimeError("canSeedPresetCachesFromBootstrap", error);
        return false;
    }
}
function getExportBucketOrders(source, key) {
    var _a, _b;
    try {
        var buckets = ((_a = source.exportOrderBuckets) !== null && _a !== void 0 ? _a : source.ExportOrderBuckets);
        if (!buckets || typeof buckets !== "object") {
            return [];
        }
        var cap = key.charAt(0).toUpperCase() + key.slice(1);
        var arr = (_b = buckets[key]) !== null && _b !== void 0 ? _b : buckets[cap];
        return Array.isArray(arr) ? arr : [];
    }
    catch (error) {
        logClientRuntimeError("getExportBucketOrders", error);
        return [];
    }
}
function getSourceOrdersForTab(source, key) {
    try {
        var fromExport = getExportBucketOrders(source, key);
        if (fromExport.length > 0) {
            return fromExport;
        }
        var slices = getSTOrderDetailsTabSlicesFromPayload(source);
        if (!slices) {
            return [];
        }
        if (!hasExportOrderBuckets(source)) {
            logClientRuntimeError("getSourceOrdersForTab.paginatedFallback", new Error("ExportOrderBuckets missing for tab \"".concat(key, "\" \u2014 preset cache may be incomplete (page-1 only).")));
        }
        var orders = slices[key].orders;
        return Array.isArray(orders) ? orders : [];
    }
    catch (error) {
        logClientRuntimeError("getSourceOrdersForTab", error);
        return [];
    }
}
function getSourceRefundedBucketOrders(source) {
    var _a, _b;
    try {
        var buckets = ((_a = source.exportOrderBuckets) !== null && _a !== void 0 ? _a : source.ExportOrderBuckets);
        if (!buckets || typeof buckets !== "object") {
            return [];
        }
        var arr = (_b = buckets.refunded) !== null && _b !== void 0 ? _b : buckets.Refunded;
        return Array.isArray(arr) ? arr : [];
    }
    catch (error) {
        logClientRuntimeError("getSourceRefundedBucketOrders", error);
        return [];
    }
}
function getSourceRefundRows(source) {
    var _a, _b;
    try {
        var buckets = ((_a = source.exportOrderBuckets) !== null && _a !== void 0 ? _a : source.ExportOrderBuckets);
        if (buckets && typeof buckets === "object") {
            var fromExport = (_b = buckets.refundRows) !== null && _b !== void 0 ? _b : buckets.RefundRows;
            if (Array.isArray(fromExport) && fromExport.length > 0) {
                return fromExport.map(function (row) { return parseStReportsRefundGridRow(row); });
            }
        }
        if (!hasExportOrderBuckets(source)) {
            logClientRuntimeError("getSourceRefundRows.paginatedFallback", new Error("ExportOrderBuckets missing — refund preset cache may be incomplete (page-1 only)."));
        }
        return getSTOrderDetailsRefundRowsFromPayload(source);
    }
    catch (error) {
        logClientRuntimeError("getSourceRefundRows", error);
        return [];
    }
}
function getRefundCalendarDateRaw(row) {
    var _a;
    try {
        var created = (_a = row.CreatedDate) !== null && _a !== void 0 ? _a : row.createdDate;
        if (isMeaningfulSTOrderDetailsDateValue(created)) {
            return String(created).trim();
        }
        return getSTOrderDetailsOrderEffectiveDateRaw(row);
    }
    catch (error) {
        logClientRuntimeError("getRefundCalendarDateRaw", error);
        return null;
    }
}
function isRefundRowInCalendarRange(row, rangeStart, rangeEnd) {
    var _a;
    try {
        var created = (_a = row.CreatedDate) !== null && _a !== void 0 ? _a : row.createdDate;
        if (!isMeaningfulSTOrderDetailsDateValue(created)) {
            return false;
        }
        var parsed = parseDisplayDateValue(created);
        if (!parsed) {
            return false;
        }
        var day = new Date(parsed);
        day.setHours(0, 0, 0, 0);
        var start = new Date(rangeStart);
        start.setHours(0, 0, 0, 0);
        var end = new Date(rangeEnd);
        end.setHours(0, 0, 0, 0);
        return day.getTime() >= start.getTime() && day.getTime() <= end.getTime();
    }
    catch (error) {
        logClientRuntimeError("isRefundRowInCalendarRange", error);
        return false;
    }
}
function isRefundOrderRowInCalendarRange(row, rangeStart, rangeEnd) {
    try {
        var raw = getRefundCalendarDateRaw(row);
        if (!raw) {
            return false;
        }
        var parsed = parseDisplayDateValue(raw);
        if (!parsed) {
            return false;
        }
        var day = new Date(parsed);
        day.setHours(0, 0, 0, 0);
        var start = new Date(rangeStart);
        start.setHours(0, 0, 0, 0);
        var end = new Date(rangeEnd);
        end.setHours(0, 0, 0, 0);
        return day.getTime() >= start.getTime() && day.getTime() <= end.getTime();
    }
    catch (error) {
        logClientRuntimeError("isRefundOrderRowInCalendarRange", error);
        return false;
    }
}
function toStReportsOrderRow(row) {
    return mapRefundGridRowToStReportsOrderRow(row);
}
function dedupeSTOrderRowKey(row) {
    var _a, _b, _c, _d, _e, _f;
    try {
        var r = row;
        var refundId = String((_b = (_a = r.refundId) !== null && _a !== void 0 ? _a : r.RefundId) !== null && _b !== void 0 ? _b : "").trim();
        if (refundId) {
            return "refund:".concat(refundId);
        }
        var orderNum = String((_d = (_c = r.orderNumber) !== null && _c !== void 0 ? _c : r.OrderNumber) !== null && _d !== void 0 ? _d : "").trim();
        var orderId = String((_f = (_e = r.orderId) !== null && _e !== void 0 ? _e : r.OrderId) !== null && _f !== void 0 ? _f : "").trim();
        return "order:".concat(orderNum, "|").concat(orderId);
    }
    catch (error) {
        logClientRuntimeError("dedupeSTOrderRowKey", error);
        return "";
    }
}
function getSTOrderRowSortTime(row) {
    try {
        var raw = getSTOrderDetailsOrderEffectiveDateRaw(row);
        if (!raw) {
            return 0;
        }
        var parsed = parseDisplayDateValue(raw);
        return parsed ? parsed.getTime() : 0;
    }
    catch (error) {
        logClientRuntimeError("getSTOrderRowSortTime", error);
        return 0;
    }
}
/** All tab includes refunds matched on `CreatedDate`; merge grid/bucket refunds not already in the order-date `all` bucket. */
function mergeRefundsIntoAllOrders(baseOrders, refundRows, refundedBucketOrders) {
    try {
        var seen = new Set(baseOrders.map(dedupeSTOrderRowKey));
        var merged = __spreadArray([], baseOrders, true);
        for (var _i = 0, refundedBucketOrders_1 = refundedBucketOrders; _i < refundedBucketOrders_1.length; _i++) {
            var row = refundedBucketOrders_1[_i];
            var key = dedupeSTOrderRowKey(row);
            if (!key || seen.has(key)) {
                continue;
            }
            seen.add(key);
            merged.push(row);
        }
        for (var _a = 0, refundRows_1 = refundRows; _a < refundRows_1.length; _a++) {
            var refund = refundRows_1[_a];
            var orderRow = toStReportsOrderRow(refund);
            var key = dedupeSTOrderRowKey(orderRow);
            if (!key || seen.has(key)) {
                continue;
            }
            seen.add(key);
            merged.push(orderRow);
        }
        merged.sort(function (a, b) { return getSTOrderRowSortTime(b) - getSTOrderRowSortTime(a); });
        return merged;
    }
    catch (error) {
        logClientRuntimeError("mergeRefundsIntoAllOrders", error);
        return baseOrders;
    }
}
function sumOrderTotals(orders) {
    var _a, _b, _c, _d;
    try {
        var subtotal = 0;
        var commission = 0;
        for (var _i = 0, orders_1 = orders; _i < orders_1.length; _i++) {
            var row = orders_1[_i];
            var r = row;
            var s = Number((_b = (_a = r.subtotal) !== null && _a !== void 0 ? _a : r.Subtotal) !== null && _b !== void 0 ? _b : 0);
            var c = Number((_d = (_c = r.commission) !== null && _c !== void 0 ? _c : r.Commission) !== null && _d !== void 0 ? _d : 0);
            if (Number.isFinite(s)) {
                subtotal += s;
            }
            if (Number.isFinite(c)) {
                commission += c;
            }
        }
        return { subtotal: subtotal, commission: commission };
    }
    catch (error) {
        logClientRuntimeError("sumOrderTotals", error);
        return { subtotal: 0, commission: 0 };
    }
}
function buildOrderTabSlice(filtered, pageSize) {
    try {
        return {
            orders: filtered.slice(0, pageSize),
            totalCount: filtered.length,
            pageNumber: 1,
            pageSize: pageSize,
            hasNextPage: filtered.length > pageSize,
        };
    }
    catch (error) {
        logClientRuntimeError("buildOrderTabSlice", error);
        return {
            orders: [],
            totalCount: 0,
            pageNumber: 1,
            pageSize: pageSize,
            hasNextPage: false,
        };
    }
}
function buildRefundTabSlice(filtered, pageSize) {
    try {
        return {
            refundOrders: filtered.slice(0, pageSize),
            totalCount: filtered.length,
            pageNumber: 1,
            pageSize: pageSize,
            hasNextPage: filtered.length > pageSize,
        };
    }
    catch (error) {
        logClientRuntimeError("buildRefundTabSlice", error);
        return {
            refundOrders: [],
            totalCount: 0,
            pageNumber: 1,
            pageSize: pageSize,
            hasNextPage: false,
        };
    }
}
/**
 * Client-side filter of the 30-day bootstrap payload into a smaller preset range.
 * Prefers export-mode bootstrap (`ExportOrderBuckets`); falls back to paginated `TabOrderSlices` when export buckets are absent.
 * Orders match on paid date when set, else order date (`MM/dd/yyyy`).
 * Refunds use `CreatedDate`; matching refunds are merged into the `all` slice so the All tab matches the API.
 */
export function buildSTOrderDetailsFilteredPayloadFromBootstrap(sourcePayload, rangeStart, rangeEnd, pageSize) {
    var _a, _b, _c;
    try {
        var prepared = prepareSTOrderDetailsReportsPayload(sourcePayload);
        if (!prepared) {
            return null;
        }
        var filterOrder = function (row) {
            return isSTOrderDetailsOrderInCalendarRange(row, rangeStart, rangeEnd);
        };
        var filteredAllBase = getSourceOrdersForTab(prepared, "all").filter(filterOrder);
        var filteredPaid = getSourceOrdersForTab(prepared, "paid").filter(filterOrder);
        var filteredUnpaid = getSourceOrdersForTab(prepared, "unpaid").filter(filterOrder);
        var filteredDraft = getSourceOrdersForTab(prepared, "draft").filter(filterOrder);
        var filteredRefunds = getSourceRefundRows(prepared).filter(function (row) {
            return isRefundRowInCalendarRange(row, rangeStart, rangeEnd);
        });
        var filteredRefundedBucket = getSourceRefundedBucketOrders(prepared).filter(function (row) {
            return isRefundOrderRowInCalendarRange(row, rangeStart, rangeEnd);
        });
        var filteredAll = mergeRefundsIntoAllOrders(filteredAllBase, filteredRefunds, filteredRefundedBucket);
        var allSlice = buildOrderTabSlice(filteredAll, pageSize);
        var paidSlice = buildOrderTabSlice(filteredPaid, pageSize);
        var unpaidSlice = buildOrderTabSlice(filteredUnpaid, pageSize);
        var draftSlice = buildOrderTabSlice(filteredDraft, pageSize);
        var refundSlice = buildRefundTabSlice(filteredRefunds, pageSize);
        var totals = sumOrderTotals(filteredAll);
        var currency = String((_b = (_a = prepared.currency) !== null && _a !== void 0 ? _a : prepared.Currency) !== null && _b !== void 0 ? _b : "");
        var filteredPayload = __assign(__assign({}, cloneSTOrderDetailsReportsPayload(prepared)), { Orders: allSlice.orders, orders: allSlice.orders, TotalCount: filteredAll.length, totalCount: filteredAll.length, TotalSubtotal: totals.subtotal, totalSubtotal: totals.subtotal, TotalCommission: totals.commission, totalCommission: totals.commission, Currency: currency, currency: currency, paidCount: filteredPaid.length, PaidCount: filteredPaid.length, unpaidCount: filteredUnpaid.length, UnpaidCount: filteredUnpaid.length, draftCount: filteredDraft.length, DraftCount: filteredDraft.length, refundedCount: filteredRefunds.length, RefundedCount: filteredRefunds.length, refundTotalCount: filteredRefunds.length, RefundTotalCount: filteredRefunds.length, pageNumber: 1, pageSize: pageSize, hasNextPage: allSlice.hasNextPage, activeTab: "All", ActiveTab: "All", tabOrderSlices: {
                all: allSlice,
                paid: paidSlice,
                unpaid: unpaidSlice,
                draft: draftSlice,
                refunded: refundSlice,
            }, TabOrderSlices: {
                All: allSlice,
                Paid: paidSlice,
                Unpaid: unpaidSlice,
                Draft: draftSlice,
                Refunded: refundSlice,
            }, exportOrderBuckets: {
                all: filteredAll,
                paid: filteredPaid,
                unpaid: filteredUnpaid,
                draft: filteredDraft,
                refunded: filteredRefundedBucket.length > 0
                    ? filteredRefundedBucket
                    : filteredRefunds.map(toStReportsOrderRow),
                refundRows: filteredRefunds,
            }, ExportOrderBuckets: {
                All: filteredAll,
                Paid: filteredPaid,
                Unpaid: filteredUnpaid,
                Draft: filteredDraft,
                Refunded: filteredRefundedBucket.length > 0
                    ? filteredRefundedBucket
                    : filteredRefunds.map(toStReportsOrderRow),
                RefundRows: filteredRefunds,
            } });
        return (_c = prepareSTOrderDetailsReportsPayload(filteredPayload)) !== null && _c !== void 0 ? _c : filteredPayload;
    }
    catch (error) {
        logClientRuntimeError("buildSTOrderDetailsFilteredPayloadFromBootstrap", error);
        return null;
    }
}
/**
 * Writes Today / Yesterday / Last 7 Days cache entries from the 30-day bootstrap response.
 * Prefer export-mode (`ExportOrderBuckets`); list-mode falls back to `TabOrderSlices` (page 1) with a warning.
 */
export function seedSTOrderDetailsPresetCachesFromThirtyDayBootstrap(dispatch, sourcePayload, pageSize) {
    try {
        if (!Number.isFinite(pageSize) || pageSize <= 0) {
            return;
        }
        if (!canSeedPresetCachesFromBootstrap(sourcePayload)) {
            logClientRuntimeError("seedSTOrderDetailsPresetCachesFromThirtyDayBootstrap", new Error("Bootstrap payload has no export buckets or tab slices — skipping preset cache seed."));
            return;
        }
        if (!hasExportOrderBuckets(sourcePayload)) {
            logClientRuntimeError("seedSTOrderDetailsPresetCachesFromThirtyDayBootstrap.paginatedFallback", new Error("ExportOrderBuckets missing — seeding presets from TabOrderSlices (page-1 only)."));
        }
        for (var _i = 0, _a = getSTOrderDetailsBootstrapPresetRanges(); _i < _a.length; _i++) {
            var preset = _a[_i];
            try {
                var filtered = buildSTOrderDetailsFilteredPayloadFromBootstrap(sourcePayload, preset.startDate, preset.endDate, pageSize);
                if (!filtered) {
                    continue;
                }
                dispatch({
                    type: SalesRepDataActionType.ST_REPORTS_ORDERS_CACHE,
                    payload: {
                        rangeKey: buildSTReportsOrdersCacheRangeKey(preset.dateFrom, preset.dateTo, pageSize, ""),
                        tab: "All",
                        pageNumber: 1,
                        pageSize: pageSize,
                        searchQuery: "",
                        apiPayload: filtered,
                    },
                });
            }
            catch (error) {
                logClientRuntimeError("seedSTOrderDetailsPresetCachesFromThirtyDayBootstrap.preset", error);
            }
        }
    }
    catch (error) {
        logClientRuntimeError("seedSTOrderDetailsPresetCachesFromThirtyDayBootstrap", error);
    }
}
